import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { useEffect } from "react";
import { Check, ArrowRight } from "lucide-react";
import { Reveal, MaskedLine, SectionLabel } from "@/components/Reveal";
import { services, IMAGES, PHONE_TEL, usePageTitle } from "@/lib/data";

export default function Services() {
  usePageTitle("Services");
  const { hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 300);
    }
  }, [hash]);
  return (
    <>
      <section data-testid="services-hero" className="pt-44 pb-24 grain relative overflow-hidden">
        <div className="relative z-[2] mx-auto max-w-7xl px-6 lg:px-12">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9, delay: 0.15 }}
            className="text-[11px] tracking-[0.3em] uppercase font-semibold text-sage-deep mb-8"
          >
            How we can help
          </motion.p>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.98] font-medium text-night max-w-4xl" data-testid="services-headline">
            <MaskedLine delay={0.25}>Every tangled situation</MaskedLine>
            <MaskedLine delay={0.42}>
              has a <em className="not-italic text-sage-deep">way through.</em>
            </MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="mt-8 text-lg leading-relaxed text-charcoal max-w-2xl"
          >
            Below is what each support area looks like in practice — what we do, and the typical
            next steps. Every engagement starts the same way: a free, private conversation.
          </motion.p>
        </div>
      </section>

      <section data-testid="services-list" className="pb-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-12">
          {services.map((s, i) => (
            <Reveal key={s.id}>
              <article
                id={s.id}
                data-testid={`service-block-${s.id}`}
                className="grid lg:grid-cols-12 gap-10 lg:gap-14 py-16 lg:py-20 border-t border-sage/25 scroll-mt-28"
              >
                <div className="lg:col-span-7">
                  <span className="font-serif text-5xl text-sage/50 leading-none">{String(i + 1).padStart(2, "0")}</span>
                  <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-night mt-4">{s.title}</h2>
                  <p className="font-serif italic text-xl text-coastal-deep mt-2">{s.tagline}</p>
                  <p className="mt-6 text-charcoal leading-relaxed text-lg">{s.description}</p>
                  <ul className="mt-8 space-y-3">
                    {s.help.map((h) => (
                      <li key={h} className="flex items-start gap-3 text-charcoal">
                        <Check size={17} strokeWidth={2} className="text-sage-deep mt-1 shrink-0" />
                        <span className="leading-relaxed">{h}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="lg:col-span-5">
                  <div className={`rounded-3xl p-8 lg:p-10 ${i % 2 === 0 ? "bg-bone" : "bg-coastal-pale"}`}>
                    <p className="text-[11px] tracking-[0.24em] uppercase font-semibold text-sage-deep mb-6">
                      Typical next steps
                    </p>
                    <ol className="space-y-6">
                      {s.steps.map((step, j) => (
                        <li key={step} className="flex items-start gap-4">
                          <span className="font-serif text-2xl text-sage-deep leading-none mt-0.5 shrink-0 w-7">{j + 1}.</span>
                          <span className="text-charcoal leading-relaxed">{step}</span>
                        </li>
                      ))}
                    </ol>
                    <Link
                      to="/contact"
                      data-testid={`service-cta-${s.id}`}
                      className="mt-8 inline-flex items-center gap-2 text-sm font-medium text-night hover:text-sage-deep transition-colors duration-300"
                    >
                      Start with a private conversation <ArrowRight size={14} strokeWidth={2} />
                    </Link>
                  </div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <section data-testid="services-cta" className="relative bg-night text-ivory py-24 grain overflow-hidden">
        <img src={IMAGES.morning} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-b from-night/60 via-night/40 to-night/80" />
        <div className="relative z-[2] mx-auto max-w-3xl px-6 text-center">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight">
              Not sure which situation fits yours?
            </h2>
            <p className="mt-6 text-ivory/70 text-lg leading-relaxed">
              That's completely normal — most aren't one thing. Tell us what's happening and
              we'll sort it out together, privately.
            </p>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <Link
                to="/contact"
                data-testid="services-cta-form-button"
                className="rounded-full bg-ivory text-night px-8 py-4 text-sm tracking-wide hover:bg-sage-pale hover:scale-[1.03] transition-all duration-300"
              >
                Send a private note
              </Link>
              <a
                href={PHONE_TEL}
                data-testid="services-cta-call-button"
                className="rounded-full border border-ivory/30 text-ivory px-8 py-4 text-sm tracking-wide hover:border-sage hover:text-sage transition-all duration-300"
              >
                Call us instead
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
